import { useState } from 'react';

interface PlagiarismPanelProps {
  content: string;
}

export default function PlagiarismPanel({ content }: PlagiarismPanelProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);

  // Mock plagiarism data
  const plagiarismScore = 18; // percentage
  const uniqueScore = 82;
  const matchesFound = 3;
  const totalSources = 94238756;

  const matches = [
    {
      text: 'Climate change represents one of the most pressing challenges facing humanity in the 21st century.',
      source: 'Nature Climate Change Journal',
      url: 'https://nature.com/articles/climate-2023',
      similarity: 94,
      type: 'Exact Match'
    },
    {
      text: 'The greenhouse effect is a natural process that warms the Earth\'s surface.',
      source: 'Environmental Science Quarterly',
      url: 'https://esq.org/greenhouse-effect',
      similarity: 87,
      type: 'Paraphrased'
    },
    {
      text: 'Rising global temperatures have led to increased frequency of extreme weather events.',
      source: 'IPCC Climate Report 2023',
      url: 'https://ipcc.ch/report-2023',
      similarity: 91,
      type: 'Exact Match'
    }
  ];

  const handleScan = () => {
    setIsScanning(true);
    setScanComplete(false);
    setAnimatedScore(0);

    // Simulate scanning
    setTimeout(() => {
      setIsScanning(false);
      setScanComplete(true);
      
      // Animate score from 0 to final value
      const duration = 1200;
      const steps = 60;
      const increment = plagiarismScore / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= plagiarismScore) {
          setAnimatedScore(plagiarismScore);
          clearInterval(timer);
        } else {
          setAnimatedScore(Math.floor(current));
        }
      }, duration / steps);
    }, 2500);
  };

  // Get color based on score
  const getScoreColor = (score: number) => {
    if (score <= 20) return { ring: '#10b981', bg: '#d1fae5', text: '#065f46' }; // green
    if (score <= 40) return { ring: '#f59e0b', bg: '#fef3c7', text: '#92400e' }; // amber
    if (score <= 60) return { ring: '#f97316', bg: '#fed7aa', text: '#9a3412' }; // orange
    return { ring: '#ef4444', bg: '#fecaca', text: '#991b1b' }; // red
  };

  const getVerdict = (score: number) => {
    if (score <= 20) return { label: 'Low Risk', color: 'bg-emerald-100 text-emerald-700' };
    if (score <= 40) return { label: 'Moderate', color: 'bg-amber-100 text-amber-700' };
    if (score <= 60) return { label: 'High Risk', color: 'bg-orange-100 text-orange-700' };
    return { label: 'Critical', color: 'bg-red-100 text-red-700' };
  };

  const colors = getScoreColor(animatedScore);
  const verdict = getVerdict(plagiarismScore);

  // SVG circle calculations
  const radius = 85;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Plagiarism Checker</h3>
            <p className="text-sm text-gray-500 mt-0.5">Scan against {totalSources.toLocaleString()}+ sources</p>
          </div>
          <button
            onClick={handleScan}
            disabled={isScanning || !content}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
          >
            {isScanning ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Scanning...
              </>
            ) : (
              <>
                <i className="ri-search-line"></i>
                Scan for Plagiarism
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!isScanning && !scanComplete && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-4xl text-purple-600"></i>
            </div>
            <h4 className="text-base font-semibold text-gray-900 mb-2">Ready to Scan</h4>
            <p className="text-sm text-gray-500 max-w-sm">
              Click "Scan for Plagiarism" to check your paper against millions of academic sources, journals, and web content.
            </p>
          </div>
        )}

        {isScanning && (
          <div className="flex flex-col items-center justify-center h-full">
            {/* Scanning pulse ring */}
            <div className="relative w-64 h-64 flex items-center justify-center mb-6">
              <svg className="w-full h-full -rotate-90">
                {/* Background circle */}
                <circle
                  cx="128"
                  cy="128"
                  r={radius}
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                />
                {/* Animated pulse ring */}
                <circle
                  cx="128"
                  cy="128"
                  r={radius}
                  fill="none"
                  stroke="#a855f7"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * 0.75}
                  className="animate-pulse"
                  style={{
                    animation: 'spin 2s linear infinite, pulse 1.5s ease-in-out infinite'
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <i className="ri-loader-4-line text-5xl text-purple-600 animate-spin mb-2"></i>
                <span className="text-sm font-medium text-gray-600">Scanning...</span>
              </div>
            </div>
            <p className="text-sm text-gray-500">Checking against {totalSources.toLocaleString()}+ sources</p>
          </div>
        )}

        {scanComplete && (
          <div className="space-y-6">
            {/* Progress Ring Visualization */}
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8">
              <div className="flex flex-col items-center">
                {/* Main Progress Ring */}
                <div className="relative w-64 h-64 flex items-center justify-center mb-6">
                  <svg className="w-full h-full -rotate-90">
                    {/* Background circle */}
                    <circle
                      cx="128"
                      cy="128"
                      r={radius}
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                    />
                    {/* Animated progress circle */}
                    <circle
                      cx="128"
                      cy="128"
                      r={radius}
                      fill="none"
                      stroke={colors.ring}
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      style={{
                        transition: 'stroke-dashoffset 1.2s ease-out, stroke 0.3s ease'
                      }}
                    />
                  </svg>
                  
                  {/* Center content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-5xl font-bold mb-1" style={{ color: colors.ring }}>
                      {animatedScore}%
                    </div>
                    <div className="text-sm font-medium text-gray-600 mb-3">Plagiarized</div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${verdict.color}`}>
                      {verdict.label}
                    </div>
                  </div>
                </div>

                {/* Stats Pills */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-emerald-700">{uniqueScore}% Unique</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-full">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-red-700">{matchesFound} Matches</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Breakdown Chart */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Match Breakdown</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Exact Matches</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: '12%' }}></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-10 text-right">12%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Paraphrased</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 rounded-full" style={{ width: '6%' }}></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-10 text-right">6%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Unique Content</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: '82%' }}></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-10 text-right">82%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Matches List */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Detected Matches ({matchesFound})</h4>
              <div className="space-y-4">
                {matches.map((match, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          match.type === 'Exact Match' 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {match.type}
                        </span>
                        <span className="text-xs font-semibold text-purple-600">{match.similarity}% Similar</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-3 leading-relaxed bg-yellow-50 border-l-4 border-yellow-400 pl-3 py-2">
                      "{match.text}"
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <i className="ri-file-text-line"></i>
                        <span className="font-medium">{match.source}</span>
                      </div>
                      <a
                        href={match.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 whitespace-nowrap"
                      >
                        View Source
                        <i className="ri-external-link-line"></i>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rescan Button */}
            <button
              onClick={handleScan}
              className="w-full px-4 py-3 bg-white border-2 border-purple-600 text-purple-600 text-sm font-semibold rounded-lg hover:bg-purple-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-refresh-line"></i>
              Rescan Paper
            </button>
          </div>
        )}
      </div>
    </div>
  );
}